import { create } from 'zustand';
import useBatchStore from './batchStore';
import {
  getStudentsByBatch,
  addStudentToBatch,
  deleteStudent,
  bulkAddStudents,
  updateStudent as updateStudentService,
} from '../services/studentService';

const useStudentStore = create((set, get) => ({
  students: [],
  loading: false,
  error: null,
  deleting: null,
  _fetchedBatchId: null,

  fetchStudents: async (batchId, force = false) => {
    if (!force && get()._fetchedBatchId === batchId && get().students.length > 0) return;
    set({ loading: true, error: null });
    try {
      const { data } = await getStudentsByBatch(batchId);
      set({ students: data.students || [], _fetchedBatchId: batchId });
    } catch (e) {
      set({ error: e?.response?.data?.message || e.message });
    } finally {
      set({ loading: false });
    }
  },

  addStudent: async (batchId, form) => {
    const { data } = await addStudentToBatch(batchId, form);
    set((s) => ({ students: [...s.students, data.student] }));
    await useBatchStore.getState().fetchBatchById(batchId);
    await useBatchStore.getState().fetchBatches(true);
    return data.student;
  },

  bulkAdd: async (batchId, students) => {
    const { data } = await bulkAddStudents(batchId, students);
    // Refetch since bulk returns a summary, not the full updated list
    const res = await getStudentsByBatch(batchId);
    set({ students: res.data.students || [], _fetchedBatchId: batchId });
    return data;
  },

  removeStudent: async (studentId) => {
    set({ deleting: studentId });
    try {
      await deleteStudent(studentId);
      const student = get().students.find((s) => s._id === studentId);
      const batchId = student?.batch || student?.batchId;
      set((s) => ({ students: s.students.filter((st) => st._id !== studentId) }));
      if (batchId) {
        await useBatchStore.getState().fetchBatchById(batchId);
        await useBatchStore.getState().fetchBatches(true);
      }
    } finally {
      set({ deleting: null });
    }
  },

  updateStudent: async (studentId, data) => {
    const batchId = get()._fetchedBatchId;
    const res = await updateStudentService(batchId, studentId, data);
    set((state) => ({
      students: state.students.map((s) =>
        s._id === studentId ? res.data.student ?? res.data : s
      ),
    }));
  },
}));

export default useStudentStore;
